import type { Mark } from './marks';

/**
 * Where a candidate's highlights and notes live during a sitting.
 *
 * They used to live nowhere. Each pane held its marks in component state and
 * threw them away the moment it was shown a different part — which is every
 * time a candidate moves between Listening parts or Reading passages, and the
 * one thing a candidate does constantly. They would mark the dates in passage
 * one, check something in passage two, come back, and find the passage bare.
 * A highlight you cannot return to is not worth drawing.
 *
 * Marks are the candidate's own working, not their answers: nothing here is
 * sent to the server or scored. They are kept per pane and per part — the
 * question pane's marks are not the passage's — and they are kept for as long
 * as the tab is open, so a reload in the middle of a paper does not cost them
 * either.
 */

/**
 * sessionStorage rather than localStorage: it is per tab and it ends with the
 * tab, so one candidate's marking cannot survive into the next candidate's
 * sitting on a shared machine in a centre.
 */
const STORE = 'target.marks.v2';

/**
 * Positions stored under v1 were counted with the text of answer boxes in
 * them, so they point at the wrong words now that those are left out. They are
 * dropped rather than read: a highlight that has gone is better than one that
 * has quietly moved onto something the candidate never marked.
 */
try {
  window.sessionStorage.removeItem('target.marks.v1');
} catch {
  // Storage refused: there is nothing in it to clear.
}

/**
 * Held in memory as well.
 *
 * Storage can refuse — a private window, a locked-down machine, a browser with
 * site data blocked — and a candidate on one of those should still keep their
 * highlights while the paper is open. Memory is the working copy; storage is
 * what survives a reload, when it is allowed to.
 */
const memory = new Map<string, Mark[]>();

function fromStorage(): Record<string, Mark[]> {
  try {
    const raw = window.sessionStorage.getItem(STORE);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, Mark[]>) : {};
  } catch {
    return {};
  }
}

function toStorage(all: Record<string, Mark[]>): void {
  try {
    window.sessionStorage.setItem(STORE, JSON.stringify(all));
  } catch {
    // Out of quota, or storage refused outright. The marks are still in
    // memory, so the paper carries on; only a reload would lose them.
  }
}

/** A mark as it comes back from storage, which is to say: not to be trusted. */
function sound(value: unknown): value is Mark {
  if (!value || typeof value !== 'object') return false;
  const m = value as Partial<Mark>;
  return (
    typeof m.id === 'string' &&
    typeof m.start === 'number' &&
    typeof m.end === 'number' &&
    Number.isFinite(m.start) &&
    Number.isFinite(m.end) &&
    m.start < m.end &&
    (m.note === undefined || typeof m.note === 'string')
  );
}

/** The marks for one pane of one part. */
export function marksFor(key: string): Mark[] {
  const held = memory.get(key);
  if (held) return held;

  const stored = fromStorage()[key];
  const marks = Array.isArray(stored) ? stored.filter(sound) : [];
  memory.set(key, marks);
  return marks;
}

/** Records the marks for one pane of one part, replacing what was there. */
export function rememberMarks(key: string, marks: Mark[]): void {
  memory.set(key, marks);
  const all = fromStorage();
  if (marks.length === 0) delete all[key];
  else all[key] = marks;
  toStorage(all);
}

/**
 * Forgets everything. Called when a paper is handed in: the next candidate at
 * this machine starts on clean text, and a finished paper's marking is of no
 * further use to anyone.
 */
export function forgetMarks(): void {
  memory.clear();
  try {
    window.sessionStorage.removeItem(STORE);
  } catch {
    // Nothing to do: memory is cleared either way.
  }
}
